/**
 * Project Profitability Report
 * Budget vs Actual comparison with profit margin analysis
 * Task 34: Project Management - Profitability Reports
 * For: agencies, consulting, IT, photographers
 */

import { useState, useMemo } from 'react';
import { IndustryProject, ProjectStatus } from '@/types/industry';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ProjectProfitabilityReportProps {
  projects: IndustryProject[];
  clientMap?: Record<number, string>;
  onExportPDF?: () => void;
  onExportCSV?: () => void;
}

type PeriodFilter = 'all' | 'current_month' | 'last_month' | 'current_quarter' | 'current_year';

interface ProjectMetrics {
  project: IndustryProject;
  budgetRevenue: number;
  actualRevenue: number;
  budgetCost: number;
  actualCost: number;
  budgetProfit: number;
  actualProfit: number;
  budgetMargin: number;
  actualMargin: number;
  revenueVariance: number;
  costVariance: number;
  profitVariance: number;
  status: 'excellent' | 'good' | 'warning' | 'poor';
}

export function ProjectProfitabilityReport({
  projects,
  clientMap = {},
  onExportPDF,
  onExportCSV,
}: ProjectProfitabilityReportProps) {
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');

  // Calculate metrics for each project
  const projectMetrics = useMemo(() => {
    return projects.map((project): ProjectMetrics => {
      const budgetRevenue = project.budget_revenue || 0;
      const actualRevenue = project.actual_revenue || 0;
      const budgetCost = project.budget_cost || 0;
      const actualCost = project.actual_cost || 0;

      const budgetProfit = budgetRevenue - budgetCost;
      const actualProfit = actualRevenue - actualCost;

      const budgetMargin = budgetRevenue > 0 ? (budgetProfit / budgetRevenue) * 100 : 0;
      const actualMargin = actualRevenue > 0 ? (actualProfit / actualRevenue) * 100 : 0;

      const revenueVariance = budgetRevenue > 0 ? ((actualRevenue - budgetRevenue) / budgetRevenue) * 100 : 0;
      const costVariance = budgetCost > 0 ? ((actualCost - budgetCost) / budgetCost) * 100 : 0;
      const profitVariance = budgetProfit > 0 ? ((actualProfit - budgetProfit) / budgetProfit) * 100 : 0;

      // Determine status based on profit margin
      let status: ProjectMetrics['status'];
      if (actualMargin >= 30) status = 'excellent';
      else if (actualMargin >= 15) status = 'good';
      else if (actualMargin >= 5) status = 'warning';
      else status = 'poor';

      return {
        project,
        budgetRevenue,
        actualRevenue,
        budgetCost,
        actualCost,
        budgetProfit,
        actualProfit,
        budgetMargin,
        actualMargin,
        revenueVariance,
        costVariance,
        profitVariance,
        status,
      };
    });
  }, [projects]);

  // Filter by period
  const filteredByPeriod = useMemo(() => {
    if (periodFilter === 'all') return projectMetrics;

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return projectMetrics.filter(({ project }) => {
      if (!project.start_date) return false;
      const startDate = new Date(project.start_date);

      switch (periodFilter) {
        case 'current_month':
          return startDate.getMonth() === currentMonth && startDate.getFullYear() === currentYear;
        case 'last_month':
          const lastMonth = new Date(currentYear, currentMonth - 1);
          return startDate.getMonth() === lastMonth.getMonth() && startDate.getFullYear() === lastMonth.getFullYear();
        case 'current_quarter':
          const quarter = Math.floor(currentMonth / 3);
          const startQuarterMonth = quarter * 3;
          return (
            startDate.getMonth() >= startQuarterMonth &&
            startDate.getMonth() < startQuarterMonth + 3 &&
            startDate.getFullYear() === currentYear
          );
        case 'current_year':
          return startDate.getFullYear() === currentYear;
        default:
          return true;
      }
    });
  }, [projectMetrics, periodFilter]);

  // Filter by status
  const filteredMetrics = useMemo(() => {
    if (statusFilter === 'all') return filteredByPeriod;
    return filteredByPeriod.filter(({ project }) => project.status === statusFilter);
  }, [filteredByPeriod, statusFilter]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredMetrics.reduce(
      (acc, metrics) => ({
        budgetRevenue: acc.budgetRevenue + metrics.budgetRevenue,
        actualRevenue: acc.actualRevenue + metrics.actualRevenue,
        budgetCost: acc.budgetCost + metrics.budgetCost,
        actualCost: acc.actualCost + metrics.actualCost,
        budgetProfit: acc.budgetProfit + metrics.budgetProfit,
        actualProfit: acc.actualProfit + metrics.actualProfit,
      }),
      { budgetRevenue: 0, actualRevenue: 0, budgetCost: 0, actualCost: 0, budgetProfit: 0, actualProfit: 0 }
    );
  }, [filteredMetrics]);

  const totalBudgetMargin = totals.budgetRevenue > 0 ? (totals.budgetProfit / totals.budgetRevenue) * 100 : 0;
  const totalActualMargin = totals.actualRevenue > 0 ? (totals.actualProfit / totals.actualRevenue) * 100 : 0;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    const formatted = value.toFixed(1);
    return value >= 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Get status badge variant
  const getStatusVariant = (status: ProjectMetrics['status']) => {
    switch (status) {
      case 'excellent':
        return 'default';
      case 'good':
        return 'secondary';
      case 'warning':
        return 'outline';
      case 'poor':
        return 'destructive';
    }
  };

  // Get status label
  const getStatusLabel = (status: ProjectMetrics['status']) => {
    switch (status) {
      case 'excellent':
        return 'Excelente';
      case 'good':
        return 'Boa';
      case 'warning':
        return 'Atenção';
      case 'poor':
        return 'Crítica';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Relatório de Lucratividade</h2>
          <p className="text-muted-foreground">Análise de Budget vs Realizado</p>
        </div>
        <div className="flex gap-2">
          {onExportCSV && (
            <Button variant="outline" size="sm" onClick={onExportCSV}>
              <FileText className="mr-2 h-4 w-4" />
              Exportar CSV
            </Button>
          )}
          {onExportPDF && (
            <Button variant="outline" size="sm" onClick={onExportPDF}>
              <Download className="mr-2 h-4 w-4" />
              Exportar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select value={periodFilter} onValueChange={(val) => setPeriodFilter(val as PeriodFilter)}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os períodos</SelectItem>
            <SelectItem value="current_month">Mês atual</SelectItem>
            <SelectItem value="last_month">Mês passado</SelectItem>
            <SelectItem value="current_quarter">Trimestre atual</SelectItem>
            <SelectItem value="current_year">Ano atual</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val as ProjectStatus | 'all')}>
          <SelectTrigger>
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="planning">Planejamento</SelectItem>
            <SelectItem value="active">Ativo</SelectItem>
            <SelectItem value="completed">Concluído</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Receita Total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Budget:</span>
                <span className="font-medium">{formatCurrency(totals.budgetRevenue)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Realizado:</span>
                <span className="font-bold text-lg">{formatCurrency(totals.actualRevenue)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Lucro Total</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Budget:</span>
                <span className="font-medium">{formatCurrency(totals.budgetProfit)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Realizado:</span>
                <span className="font-bold text-lg">
                  {formatCurrency(totals.actualProfit)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Margem de Lucro</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Budget:</span>
                <span className="font-medium">{totalBudgetMargin.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Realizado:</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg">{totalActualMargin.toFixed(1)}%</span>
                  {totalActualMargin >= totalBudgetMargin ? (
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-600" />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle>Projetos ({filteredMetrics.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Projeto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Receita Budget</TableHead>
                  <TableHead className="text-right">Receita Real</TableHead>
                  <TableHead className="text-right">Custo Real</TableHead>
                  <TableHead className="text-right">Lucro Real</TableHead>
                  <TableHead className="text-right">Margem Real</TableHead>
                  <TableHead className="text-center">Performance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMetrics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      Nenhum projeto encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMetrics.map(({ project, budgetRevenue, actualRevenue, actualCost, actualProfit, actualMargin, status }) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{project.name}</div>
                          {project.start_date && (
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(project.start_date), 'MMM yyyy', { locale: ptBR })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {project.client_id ? (
                          <span className="text-sm">{clientMap[project.client_id] || `Cliente ${project.client_id}`}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(budgetRevenue)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(actualRevenue)}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{formatCurrency(actualCost)}</TableCell>
                      <TableCell className="text-right">
                        <span className={actualProfit >= 0 ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                          {formatCurrency(actualProfit)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium">{actualMargin.toFixed(1)}%</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={getStatusVariant(status)}>{getStatusLabel(status)}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
